import {
  adminGetLearningOverview,
  adminListLearningProfiles,
  adminSetLearningSkillLevel,
  adminListUnsentReports,
  adminRetryReportNotification,
} from '../../util/api';

// ================ Action types ================ //
export const FETCH_LEARNING_OVERVIEW_REQUEST = 'app/AdminLearningPage/FETCH_LEARNING_OVERVIEW_REQUEST';
export const FETCH_LEARNING_OVERVIEW_SUCCESS = 'app/AdminLearningPage/FETCH_LEARNING_OVERVIEW_SUCCESS';
export const FETCH_LEARNING_OVERVIEW_ERROR = 'app/AdminLearningPage/FETCH_LEARNING_OVERVIEW_ERROR';

export const FETCH_ADMIN_PROFILES_REQUEST = 'app/AdminLearningPage/FETCH_ADMIN_PROFILES_REQUEST';
export const FETCH_ADMIN_PROFILES_SUCCESS = 'app/AdminLearningPage/FETCH_ADMIN_PROFILES_SUCCESS';
export const FETCH_ADMIN_PROFILES_ERROR = 'app/AdminLearningPage/FETCH_ADMIN_PROFILES_ERROR';

export const ADMIN_SET_LEVEL_REQUEST = 'app/AdminLearningPage/ADMIN_SET_LEVEL_REQUEST';
export const ADMIN_SET_LEVEL_SUCCESS = 'app/AdminLearningPage/ADMIN_SET_LEVEL_SUCCESS';
export const ADMIN_SET_LEVEL_ERROR = 'app/AdminLearningPage/ADMIN_SET_LEVEL_ERROR';

export const FETCH_UNSENT_REPORTS_REQUEST = 'app/AdminLearningPage/FETCH_UNSENT_REPORTS_REQUEST';
export const FETCH_UNSENT_REPORTS_SUCCESS = 'app/AdminLearningPage/FETCH_UNSENT_REPORTS_SUCCESS';
export const FETCH_UNSENT_REPORTS_ERROR = 'app/AdminLearningPage/FETCH_UNSENT_REPORTS_ERROR';

export const RETRY_NOTIFICATION_REQUEST = 'app/AdminLearningPage/RETRY_NOTIFICATION_REQUEST';
export const RETRY_NOTIFICATION_SUCCESS = 'app/AdminLearningPage/RETRY_NOTIFICATION_SUCCESS';
export const RETRY_NOTIFICATION_ERROR = 'app/AdminLearningPage/RETRY_NOTIFICATION_ERROR';

// ================ Reducer ================ //
const initialState = {
  overview: null,
  fetchOverviewInProgress: false,
  fetchOverviewError: null,
  profiles: [],
  profilesPagination: { page: 1, limit: 20, total: 0 },
  fetchProfilesInProgress: false,
  fetchProfilesError: null,
  setLevelInProgress: false,
  setLevelError: null,
  unsentReports: [],
  fetchUnsentReportsInProgress: false,
  fetchUnsentReportsError: null,
  retryingReportId: null,
  retryNotificationError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LEARNING_OVERVIEW_REQUEST:
      return { ...state, fetchOverviewInProgress: true, fetchOverviewError: null };
    case FETCH_LEARNING_OVERVIEW_SUCCESS:
      return { ...state, fetchOverviewInProgress: false, overview: payload };
    case FETCH_LEARNING_OVERVIEW_ERROR:
      return { ...state, fetchOverviewInProgress: false, fetchOverviewError: payload };

    case FETCH_ADMIN_PROFILES_REQUEST:
      return { ...state, fetchProfilesInProgress: true, fetchProfilesError: null };
    case FETCH_ADMIN_PROFILES_SUCCESS:
      return {
        ...state,
        fetchProfilesInProgress: false,
        profiles: payload.profiles || [],
        profilesPagination: {
          page: payload.page || 1,
          limit: payload.limit || 20,
          total: payload.total || 0,
        },
      };
    case FETCH_ADMIN_PROFILES_ERROR:
      return { ...state, fetchProfilesInProgress: false, fetchProfilesError: payload };

    case ADMIN_SET_LEVEL_REQUEST:
      return { ...state, setLevelInProgress: true, setLevelError: null };
    case ADMIN_SET_LEVEL_SUCCESS:
      return { ...state, setLevelInProgress: false };
    case ADMIN_SET_LEVEL_ERROR:
      return { ...state, setLevelInProgress: false, setLevelError: payload };

    case FETCH_UNSENT_REPORTS_REQUEST:
      return { ...state, fetchUnsentReportsInProgress: true, fetchUnsentReportsError: null };
    case FETCH_UNSENT_REPORTS_SUCCESS:
      return {
        ...state,
        fetchUnsentReportsInProgress: false,
        unsentReports: payload || [],
      };
    case FETCH_UNSENT_REPORTS_ERROR:
      return { ...state, fetchUnsentReportsInProgress: false, fetchUnsentReportsError: payload };

    case RETRY_NOTIFICATION_REQUEST:
      return { ...state, retryingReportId: payload, retryNotificationError: null };
    case RETRY_NOTIFICATION_SUCCESS:
      return {
        ...state,
        retryingReportId: null,
        unsentReports: state.unsentReports.filter(r => (r._id || r.id) !== payload),
      };
    case RETRY_NOTIFICATION_ERROR:
      return { ...state, retryingReportId: null, retryNotificationError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //
export const fetchOverviewRequest = () => ({ type: FETCH_LEARNING_OVERVIEW_REQUEST });
export const fetchOverviewSuccess = payload => ({ type: FETCH_LEARNING_OVERVIEW_SUCCESS, payload });
export const fetchOverviewError = error => ({ type: FETCH_LEARNING_OVERVIEW_ERROR, payload: error });

export const fetchAdminProfilesRequest = () => ({ type: FETCH_ADMIN_PROFILES_REQUEST });
export const fetchAdminProfilesSuccess = payload => ({ type: FETCH_ADMIN_PROFILES_SUCCESS, payload });
export const fetchAdminProfilesError = error => ({ type: FETCH_ADMIN_PROFILES_ERROR, payload: error });

export const adminSetLevelRequest = () => ({ type: ADMIN_SET_LEVEL_REQUEST });
export const adminSetLevelSuccess = payload => ({ type: ADMIN_SET_LEVEL_SUCCESS, payload });
export const adminSetLevelError = error => ({ type: ADMIN_SET_LEVEL_ERROR, payload: error });

export const fetchUnsentReportsRequest = () => ({ type: FETCH_UNSENT_REPORTS_REQUEST });
export const fetchUnsentReportsSuccess = payload => ({ type: FETCH_UNSENT_REPORTS_SUCCESS, payload });
export const fetchUnsentReportsError = error => ({ type: FETCH_UNSENT_REPORTS_ERROR, payload: error });

export const retryNotificationRequest = reportId => ({ type: RETRY_NOTIFICATION_REQUEST, payload: reportId });
export const retryNotificationSuccess = reportId => ({ type: RETRY_NOTIFICATION_SUCCESS, payload: reportId });
export const retryNotificationError = error => ({ type: RETRY_NOTIFICATION_ERROR, payload: error });

// ================ Thunks ================ //
export const fetchLearningOverview = () => async (dispatch, getState) => {
  dispatch(fetchOverviewRequest());
  const currentUserId = getState()?.user?.currentUser?.id?.uuid;
  if (!currentUserId) {
    dispatch(fetchOverviewError('User not authenticated'));
    return;
  }
  return adminGetLearningOverview({ currentUserId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(fetchOverviewSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to fetch learning overview');
    })
    .catch(err => {
      dispatch(fetchOverviewError(err.message));
      throw err;
    });
};

export const fetchAdminLearningProfiles = ({ page = 1, limit = 50 } = {}) => async (dispatch, getState) => {
  dispatch(fetchAdminProfilesRequest());
  const currentUserId = getState()?.user?.currentUser?.id?.uuid;
  if (!currentUserId) {
    dispatch(fetchAdminProfilesError('User not authenticated'));
    return;
  }
  return adminListLearningProfiles({ page, limit, currentUserId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(fetchAdminProfilesSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to fetch learning profiles');
    })
    .catch(err => {
      dispatch(fetchAdminProfilesError(err.message));
      throw err;
    });
};

export const setAdminSkillLevel = payload => async (dispatch, getState) => {
  dispatch(adminSetLevelRequest());
  const currentUserId = getState()?.user?.currentUser?.id?.uuid;
  if (!currentUserId) {
    dispatch(adminSetLevelError('User not authenticated'));
    return;
  }
  return adminSetLearningSkillLevel({ ...payload, currentUserId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(adminSetLevelSuccess(data));
        // Refresh overview and profiles
        dispatch(fetchLearningOverview());
        dispatch(fetchAdminLearningProfiles());
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to adjust skill level');
    })
    .catch(err => {
      dispatch(adminSetLevelError(err.message));
      throw err;
    });
};

export const fetchAdminUnsentReports = ({ page = 1, limit = 50 } = {}) => async (dispatch, getState) => {
  dispatch(fetchUnsentReportsRequest());
  const currentUserId = getState()?.user?.currentUser?.id?.uuid;
  if (!currentUserId) {
    dispatch(fetchUnsentReportsError('User not authenticated'));
    return;
  }
  return adminListUnsentReports({ page, limit, currentUserId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(fetchUnsentReportsSuccess(data.reports || []));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to fetch unsent reports');
    })
    .catch(err => {
      dispatch(fetchUnsentReportsError(err.message));
      throw err;
    });
};

export const retryReportNotification = ({ reportId }) => async (dispatch, getState) => {
  dispatch(retryNotificationRequest(reportId));
  const currentUserId = getState()?.user?.currentUser?.id?.uuid;
  if (!currentUserId) {
    dispatch(retryNotificationError('User not authenticated'));
    return;
  }
  return adminRetryReportNotification({ reportId, currentUserId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && data.success !== false) {
        dispatch(retryNotificationSuccess(reportId));
        // Refresh overview and unsent reports
        dispatch(fetchLearningOverview());
        dispatch(fetchAdminUnsentReports());
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to retry notification');
    })
    .catch(err => {
      dispatch(retryNotificationError(err.message));
      throw err;
    });
};

export const loadData = () => (dispatch, getState) => {
  return Promise.all([
    dispatch(fetchLearningOverview()),
    dispatch(fetchAdminLearningProfiles({ page: 1, limit: 50 })),
    dispatch(fetchAdminUnsentReports({ page: 1, limit: 50 })),
  ]);
};
